/**
 * ActionProposalCard — cartão de proposta de ação do bot.
 *
 * Renderiza uma ação proposta (ex: reiniciar container) com gate de confirmação:
 * - "Confirmar": decidido pelo servidor para quem lê (`pode_confirmar`, TASK-11991);
 *   ACTION_GATES só vale para réplica antiga que não manda o campo
 * - "Recusar" disponível para qualquer membro autenticado
 * - Estados terminais (executed/failed/declined) exibem badge e ocultam botões
 *
 * Gate é conveniência de UX — backend re-valida no endpoint confirm/decline.
 */
import { Badge, Button, Card, Group, Stack, Text, Tooltip } from '@mantine/core'
import { useState, type ReactElement } from 'react'
import { IconListCheck } from '@tabler/icons-react'
import { useMediaQuery } from '@mantine/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore, useCan } from '@/store/auth'
import { useConfirmAction, useDeclineAction } from '@/hooks/useChat'
import {
  CHAVE_PEDIDO_TERRAFORM, usePedidoDoRascunho, useRetomarPedido, useTerraformChangeRequest,
} from '@/hooks/useTerraformChangeRequest'
import { AndamentoDaCriacao } from './AndamentoDaCriacao'
import { ResumoDaCriacao, type ResumoDaCriacaoDados } from './ResumoDaCriacao'
import { TicketHandoffCard } from './TicketHandoffCard'
import { SemLeituraSelo } from './SemLeituraSelo'
import { ChamadoCommentApplyCard } from './ChamadoCommentApplyCard'
import { ChamadoAssignApplyCard } from './ChamadoAssignApplyCard'
import { ChamadoFinalizarApplyCard } from './ChamadoFinalizarApplyCard'
import { FormularioDoAmbienteCard } from './FormularioDoAmbienteCard'
import { AssistantReportCard } from './AssistantReportCard'
import {
  HostOrderPlanCard,
  type HostOrderPlano,
  type HostOrderResultado,
} from './HostOrderPlanCard'
import { TerraformAdjustCard, type TerraformDiffItem } from './TerraformAdjustCard'
import { TerraformLivreCard } from './TerraformLivreCard'
import { WizardAmbiente } from '../wizard-ambiente/WizardAmbiente'

// ── Tipos ──────────────────────────────────────────────────────────────────

export interface ProposedAction {
  action_type: string
  params: Record<string, string>
  status: 'pending' | 'executed' | 'failed' | 'declined'
  /**
   * G6/Task 11 (TASK-10361): para `host_order`, calculado no SERVIDOR
   * (`chat_bot._pode_confirmar_host_order` — permissão fina + piso +
   * ambiente + Gate 0 de origem). Opcional porque as demais ações não o
   * mandam (equivalem a `true`, já gateadas por `ACTION_GATES` abaixo) —
   * `undefined` NUNCA deve ser lido como "pode confirmar" no ramo
   * `host_order`, ver `HostOrderPlanCard` mais abaixo.
   *
   * TASK-10810 (spec §5, C3 da rodada 1 de revisão): desde o portão de PRD,
   * `false` explícito também chega para `restart_container`/
   * `instance_restart`/`instance_force_restart` (produção sem laudo do
   * @DevOps para o recurso NESTE turno) — o card genérico (fora do ramo
   * `host_order`) trata isto como VETO sobre `ACTION_GATES`, não só o
   * `host_order`. `undefined` continua sem efeito (ação fora do portão, ou
   * proposta anterior a esta task).
   */
  can_confirm?: boolean
  /**
   * TASK-10810 (spec §5, I3 da rodada 1 de revisão): motivo do veto acima,
   * para o usuário SABER por que perdeu o botão — sem isto o campo era
   * write-only (gravado pelo backend, nunca lido por ninguém). Exibido no
   * tooltip do botão Confirmar quando `can_confirm === false`.
   */
  motivo_bloqueio?: string | null
  /**
   * TASK-10813: o turno NÃO produziu laudo nenhum do @DevOps — nada no cartão
   * foi lido do ambiente. Calculado no SERVIDOR
   * (`chat_bot._proposal_payload`/`ACOES_QUE_PEDEM_LEITURA`), nunca aqui.
   *
   * É SINAL, não portão: diferente de `can_confirm`, não desabilita nada. O
   * proprietário escolheu carimbar em vez de gatear (saída "(c)" do card) —
   * ver `SemLeituraSelo`. `undefined` (proposta anterior a esta task) não
   * carimba: ausência de campo não é prova de ausência de leitura.
   */
  sem_leitura?: boolean
  /**
   * MR 3 (TASK-11871): resumo do resultado que o confirm persiste
   * (`chat_bot._set_proposal_status(..., result=...)`) — hoje só `host_order`
   * com remedição da montagem do alerta. Ausente nas demais ações.
   */
  result?: HostOrderResultado | null
  /**
   * TASK-11991: decidido pelo SERVIDOR para quem LÊ, na hora da leitura
   * (`proposta_para_quem_ve.py`, passo 2c do `list_messages`) — portão de
   * chamado + `can_confirm` gravado + `authorize_confirm` do leitor, o mesmo
   * que o clique vai enfrentar. Quando presente, vence `ACTION_GATES`: o mapa
   * estático deixava fora do alcance de todos (inclusive admin) as ações que
   * não estavam nele. Ausente em réplica antiga — aí o mapa segue valendo.
   */
  pode_confirmar?: boolean
  /** TASK-11991: rótulo da ALLOWLIST do backend, para ação sem ramo em `actionLabel`. */
  label?: string
  /**
   * TASK-11991: tenant do rascunho de `terraform_stage_create` (e, desde a
   * consoleai#25762, de `abrir_formulario_de_ambiente`). `id`/`nome` `null`
   * para quem não enxerga aquele tenant.
   */
  rascunho_tenant?: { id: number | null; nome: string | null }
  /**
   * consoleai#24894: o que a criação de ambiente vai criar (serviços, banco,
   * rede, conta), montado pelo servidor só para quem enxerga o tenant.
   */
  resumo?: ResumoDaCriacaoDados
  /**
   * consoleai#24894: há um cartão mais novo do mesmo rascunho — este não se
   * confirma mais. Decidido no servidor (`proposta_para_quem_ve`).
   */
  substituida?: boolean
}

interface Props {
  conversationId: number
  messageId: number
  proposedAction: ProposedAction
}

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<ProposedAction['status'], string> = {
  pending:  'Pendente',
  executed: 'Executado',
  failed:   'Falhou',
  declined: 'Recusado',
}

const STATUS_COLORS: Record<ProposedAction['status'], string> = {
  pending:  'yellow',
  executed: 'teal',
  failed:   'red',
  declined: 'gray',
}

/** O selo da criação de ambiente: o Confirmar ABRE o pedido, nada foi criado
 * ainda — o mesmo selo e a mesma cor do cartão de ajuste
 * (`TerraformAdjustCard`, inovareTeal: informacional, DESIGN_SYSTEM §2.3). */
function selo(p: ProposedAction): { rotulo: string; cor: string } {
  if (p.action_type === 'terraform_stage_create' && p.status === 'executed') {
    return { rotulo: 'Pedido criado', cor: 'inovareTeal' }
  }
  return { rotulo: STATUS_LABELS[p.status], cor: STATUS_COLORS[p.status] }
}

// ── Gate por-ação ──────────────────────────────────────────────────────────

interface ActionGate {
  resource?: string
  action?: string
  requireDevops: boolean
  selfScoped?: boolean
}

const ACTION_GATES: Record<string, ActionGate> = {
  restart_container:      { resource: 'instance', action: 'docker_restart', requireDevops: true },
  instance_restart:       { resource: 'instance', action: 'restart',        requireDevops: true },
  instance_force_restart: { resource: 'instance', action: 'force_restart',  requireDevops: true },
  ack_alert:              { requireDevops: false, selfScoped: true },
  terraform_adjust:       { resource: 'terraform', action: 'adjust', requireDevops: true },
  terraform_ajuste_livre: { resource: 'terraform', action: 'adjust', requireDevops: true },
  // `host_order` NÃO entra aqui de propósito (G6, Task 11/TASK-10361): o
  // gate dele não é papel/permissão ESTÁTICOS — depende do VERBO e do
  // AMBIENTE do tenant, que só o servidor conhece
  // (`chat_bot._pode_confirmar_host_order`, a mesma porta que
  // `service.run_order` usa para autorizar de verdade). Um mapa aqui
  // divergiria da verdade do backend; `HostOrderPlanCard` lê
  // `proposedAction.can_confirm` direto, nunca este mapa.
}

// ── host_order: mapeia `params` (achatados pelo modelo/backend) para o
// shape que `HostOrderPlanCard` consome. Defensivo com CADA campo — nada
// aqui pode presumir presença: `plan_id`/`itens`/`estimativa_bytes` podem
// faltar (dry-run "nada a limpar", ou proposta anterior à Task 9/10 sem
// enriquecimento) e isso NUNCA pode quebrar o render (achado 2 da task).
function hostOrderPlanoFromParams(params: Record<string, unknown>): HostOrderPlano {
  const itensRaw = params.itens
  const itens = Array.isArray(itensRaw)
    ? (itensRaw as HostOrderPlano['itens'])
    : null
  const tipoPlanoRaw = params.tipo_plano
  const tipo_plano: HostOrderPlano['tipo_plano'] =
    tipoPlanoRaw === 'estimado' || tipoPlanoRaw === 'enumerado'
      ? tipoPlanoRaw
      : (itens === null ? 'estimado' : 'enumerado')
  const estimativaRaw = params.estimativa_bytes
  return {
    verbo: String(params.verbo ?? ''),
    plan_id: (params.plan_id as string | null | undefined) ?? null,
    itens,
    estimativa_bytes: typeof estimativaRaw === 'number' ? estimativaRaw : null,
    tipo_plano,
    status: typeof params.status === 'string' ? params.status : undefined,
    // Task 16: o dry-run que o agente roda ao propor carrega a validade do
    // plano (`host_order_plans.expires_at`). Defensivo como os demais campos —
    // proposta anterior à Task 16 simplesmente não a tem, e o card esconde a
    // linha em vez de mostrar "Invalid Date".
    expires_at: typeof params.expires_at === 'string' ? params.expires_at : null,
    // Degrau do verbo, do catálogo (D5). O card usa isto — e SÓ isto — para
    // saber se há plano a esperar: degrau 2 (`docker_restart` e irmãos) não
    // tem plano e não precisa de um. `undefined` (proposta anterior à
    // TASK-10361) faz o card exigir plano, que é o lado conservador.
    //
    // Não derivamos daqui por `verbo`: o catálogo é TABELA, editável na
    // Fase 2, e um mapa slug→degrau neste arquivo seria o mesmo antipadrão
    // do `ACTION_GATES` — estático e livre para divergir do servidor.
    degrau: typeof params.degrau === 'number' ? params.degrau : undefined,
    // Spec 2026-09-27 §12: em produção a ordem espera a aprovação de outro
    // integrante; o cartão consulta o estado no servidor. O `plano_sha256`
    // não vem para cá: o servidor o lê da proposta PERSISTIDA no confirm.
    segunda_aprovacao_exigida: params.segunda_aprovacao_exigida === true,
    teto_bytes: typeof params.teto_bytes === 'number' ? params.teto_bytes : null,
    comando: typeof params.comando === 'string' ? params.comando : null,
  }
}

function hostOrderHostname(params: Record<string, unknown>): string {
  return String(params.instance_name ?? params.instance_id ?? '')
}

// Ambiente. O achado ficou aberto da Task 11 até a Task 16 (TASK-10361):
// esta função existia, o `HostOrderPlanCard` implementava o portão do
// hostname digitado (spec §7/§8.6) e ele NUNCA disparava, porque nenhum
// campo de ambiente chegava em `params`. Agora chega: `_propor_host_order_
// degrau3` (backend) grava `prod` com o valor que `gates.e_producao`
// devolveu no portão 5 — a MESMA leitura que decidiu qual permissão exigir.
//
// A ordem de leitura fica como estava (o backend escreve `prod`, o primeiro
// nome): proposta ANTERIOR a esta task não tem campo nenhum e cai no
// `false`, que é o comportamento correto para ela — a checagem de hostname
// é reforço de UX, NUNCA o portão de autorização (esse é `can_confirm`,
// sempre do servidor, e revalidado por `gates.check_all` no clique).
function hostOrderIsProd(params: Record<string, unknown>): boolean {
  return Boolean(params.prod ?? params.is_prod ?? params.producao ?? false)
}

// ── terraform_adjust: mapeia `params` para o shape flat que
// `TerraformAdjustCard` consome (Task 14, TASK-10404 — ver o brief da
// task). O contrato de servidor conhecido hoje é o de
// `terraform_runner/routes.py` (`POST /change-requests`): `diff` chega como
// DOIS dicts (`{de: {...}, para: {...}}`), não como lista de
// `{campo, de, para}` — a lista é reconstruída aqui, nunca no componente,
// que só sabe renderizar o shape já achatado (é o mesmo por que o teste
// dele passa objetos já na forma final).
//
// `custo` chega em snake_case (`de_mensal`/`para_mensal`/`delta_mensal`) —
// convertido para camelCase aqui, defensivo campo a campo como
// `hostOrderPlanoFromParams` acima: uma proposta pode não ter enriquecimento
// nenhum (o `propose_action` de hoje só grava `stage_chave`/`knob`/`mapa`/
// `alvo`/`valores` — a Task 13 não liga o dry-run ao propor, diferente do
// que a Task 16 fez para `host_order`), e isso NUNCA pode quebrar o render:
// cai em diff vazio / sem custo / sem avisos, nunca em exceção.
//
// `irreversivel` NÃO é campo próprio do backend hoje — só
// `exige_repetir_valor` existe (Fase 1a: as duas coisas descrevem o MESMO
// caso, `lt.volume_size`). Deriva-se uma da outra até o dia em que o
// backend precisar separá-las (um knob irreversível sem exigir repetição,
// por exemplo) — inventar um campo que o servidor não manda seria pior que
// derivar do que ele manda.
//
// Fix round 1 (review, Critical 2): `Boolean(params.exige_repetir_valor)`
// SOZINHO conflava dois casos opostos — "o backend disse que não é
// irreversível" e "o backend não disse nada" — no mesmo `false`. Para um
// campo de SEGURANÇA isso é fail-OPEN: proposta sem o enriquecimento (o
// `propose_action` de hoje não liga o dry-run, ver acima) chegaria como
// "pode confirmar sem repetir nada", o inverso do deny-by-default que
// `hostOrderPlanoFromParams` já aplica para `host_order` (`degrau` ausente
// → exige plano, nunca libera). `dadosSegurancaAusentes` é o sinal
// SEPARADO que distingue as duas: só é `false` quando o campo VEIO do
// backend (`'exige_repetir_valor' in params`), nunca por inferência do
// valor. O card (`TerraformAdjustCard`) bloqueia Confirmar quando este
// sinal é `true`, mesmo que `irreversivel`/`exigeRepetirValor` tenham
// caído em `false` pela mesma ausência.
function terraformDiffFromParams(params: Record<string, unknown>): TerraformDiffItem[] {
  const bruto = params.diff as { de?: Record<string, unknown>; para?: Record<string, unknown> } | undefined
  const de = (bruto?.de ?? {}) as Record<string, unknown>
  const para = (bruto?.para ?? {}) as Record<string, unknown>
  const campos = Array.from(new Set([...Object.keys(de), ...Object.keys(para)]))
  return campos.map((campo) => ({ campo, de: de[campo], para: para[campo] }))
}

function terraformCustoFromParams(params: Record<string, unknown>) {
  const c = (params.custo ?? {}) as Record<string, unknown>
  return {
    deMensal: typeof c.de_mensal === 'number' ? c.de_mensal : null,
    paraMensal: typeof c.para_mensal === 'number' ? c.para_mensal : null,
    deltaMensal: typeof c.delta_mensal === 'number' ? c.delta_mensal : null,
    moeda: typeof c.moeda === 'string' ? c.moeda : 'USD',
    ressalva: typeof c.ressalva === 'string' ? c.ressalva : undefined,
  }
}

export function mapearTerraformAdjuste(params: Record<string, unknown>) {
  // Fail-closed: o campo AUSENTE (nunca gravado no payload) é distinto do
  // campo `false` (o backend afirmou que não é irreversível). `in` — não
  // `params.exige_repetir_valor === undefined` — porque um valor `null`
  // explícito também conta como "o backend não sabe dizer", o mesmo caso.
  const dadosSegurancaAusentes =
    !('exige_repetir_valor' in params) || params.exige_repetir_valor == null
  const exigeRepetirValor = Boolean(params.exige_repetir_valor)
  return {
    stageChave: String(params.stage_chave ?? ''),
    knob: String(params.knob ?? ''),
    alvo: String(params.alvo ?? ''),
    diff: terraformDiffFromParams(params),
    custo: terraformCustoFromParams(params),
    avisos: Array.isArray(params.avisos) ? (params.avisos as string[]) : [],
    irreversivel: exigeRepetirValor,
    exigeRepetirValor,
    dadosSegurancaAusentes,
  }
}

function actionLabel(proposedAction: ProposedAction): string {
  const { action_type, params } = proposedAction
  if (action_type === 'restart_container') {
    return `Reiniciar container ${params.container_name ?? '?'} em ${params.instance_id ?? '?'}`
  }
  if (action_type === 'instance_restart') {
    return `Reiniciar instância ${params.instance_id ?? '?'}`
  }
  if (action_type === 'instance_force_restart') {
    return `Power-cycle da instância ${params.instance_id ?? '?'} (para e reinicia; IP público pode mudar)`
  }
  if (action_type === 'ack_alert') {
    return `Reconhecer alerta #${params.event_id ?? '?'}`
  }
  if (action_type === 'terraform_stage_create') {
    return `Criar ambiente ${params.projeto ?? '?'}/${params.ambiente ?? '?'} — rascunho #${params.rascunho_id ?? '?'}`
  }
  if (action_type === 'terraform_conta_create') {
    // Ato 1 do padrão de ambiente novo: conta e OU vêm do servidor
    // (`derivar_ambiente`), nunca do modelo.
    return `Esta conta ainda não existe: será criada ${params.conta_nome ?? '?'} na OU ${params.ou ?? '?'}`
  }
  // TASK-11991: o fallback era `tipo: {json}` — o usuário lia o nome técnico e
  // os parâmetros em código. O rótulo vem da ALLOWLIST do servidor.
  return proposedAction.label ?? action_type
}

// ── Componente ─────────────────────────────────────────────────────────────

/** `params.aviso_fila`: a frase do lugar do pedido que nasceu na fila (spec
 * 2026-10-15 §5.2, `chat_bot._proposal_payload`). */
function avisoFilaDe(params: unknown): string | null {
  const v = (params as Record<string, unknown> | null)?.aviso_fila
  return typeof v === 'string' && v ? v : null
}

export function ActionProposalCard({ conversationId, messageId, proposedAction }: Props) {
  const { status } = proposedAction
  const isMobile = useMediaQuery('(max-width: 768px)')

  const user = useAuthStore((s) => s.user)
  const can  = useCan()
  const qc = useQueryClient()

  // D1: o pedido nasce na proposta; o cartão lê o estado dele. `null` quando
  // a ação não é ajuste ou a proposta é anterior ao fluxo (sem `pedido_id`).
  const pedidoId = (proposedAction.action_type === 'terraform_adjust'
                    || proposedAction.action_type === 'terraform_ajuste_livre')
    ? ((proposedAction.params as Record<string, unknown>)?.pedido_id as number | undefined) ?? null
    : null
  const pedidoQuery = useTerraformChangeRequest(pedidoId)
  // A criação de ambiente: o pedido do motor só existe depois do Confirmar,
  // e o cartão o acha pelo `rascunho_id` da proposta.
  const rascunhoId = proposedAction.action_type === 'terraform_stage_create'
    && proposedAction.status === 'executed'
    ? Number((proposedAction.params as Record<string, unknown>)?.rascunho_id) || null
    : null
  const pedidoDaCriacao = usePedidoDoRascunho(rascunhoId)

  const gate          = ACTION_GATES[proposedAction.action_type]
  const hasDevopsRole = !!user?.role_codes?.includes('devops')
  const hasPermission = gate?.resource && gate?.action ? can(gate.resource!, gate.action!) : true
  const canConfirmGate = !gate
    ? false
    : gate.selfScoped
      ? true
      : (!gate.requireDevops || hasDevopsRole) && hasPermission

  // TASK-10810 (spec §5, C3 da rodada 1 de revisão): `can_confirm === false`
  // do servidor é VETO sobre o gate estático acima, não só sobre o ramo
  // `host_order`. Antes desta task, o portão de PRD só afetava o cartão via
  // `host_order` — para `instance_restart`/`restart_container`/
  // `instance_force_restart` (o caminho do incidente de 14/09), o botão
  // Confirmar seguia lendo só `ACTION_GATES`, que nada sabe sobre laudo.
  // `undefined` (ação fora do portão, ou proposta anterior a esta task) não
  // veta nada — só `false` explícito veta.
  const bloqueadoPeloPortaoPrd = proposedAction.can_confirm === false
  // TASK-11991: quando o servidor decidiu para quem lê, é ele quem manda — o
  // mesmo `authorize_confirm` que o clique enfrenta. O mapa acima fica só para
  // réplica antiga, que não manda o campo.
  const servidorDecidiu = typeof proposedAction.pode_confirmar === 'boolean'
  const canConfirm = servidorDecidiu
    ? proposedAction.pode_confirmar === true
    : canConfirmGate && !bloqueadoPeloPortaoPrd

  // TASK-10813: o carimbo fica ACIMA do cartão, igual em todos os ramos, em vez
  // de virar prop de cada componente de cartão. Um lugar só para posicioná-lo é
  // o que impede o selo de aparecer em altura diferente conforme a ação — e
  // `TicketHandoffCard`/`HostOrderPlanCard` seguem sem saber que ele existe.
  // A criação de ambiente nunca leva o carimbo: o ambiente ainda não existe e
  // nenhum laudo o cobriria (spec 2026-10-09 F1.6) — o servidor já o tira, e
  // isto cobre réplica antiga.
  const carimbo = proposedAction.sem_leitura === true
    && proposedAction.action_type !== 'terraform_stage_create'
    ? <SemLeituraSelo /> : null
  const comSelo = (cartao: ReactElement) =>
    carimbo ? <Stack gap={6} style={{ maxWidth: 480 }}>{carimbo}{cartao}</Stack> : cartao

  const confirmMutation = useConfirmAction(conversationId)
  const declineMutation = useDeclineAction(conversationId)
  // Spec 2026-10-15 D3: o "Quero seguir" do pedido guardado — rota do pedido,
  // não da mensagem (a decisão é da identidade de quem pediu).
  const retomarMutation = useRetomarPedido()
  const retomar = pedidoId == null ? undefined : () => retomarMutation.mutate(pedidoId)

  const substituida = proposedAction.substituida === true
  const isPending = status === 'pending' && !substituida
  // Spec 2026-10-09 §5: o wizard abre SÓ por este botão, nunca sozinho, e só
  // para quem enxerga o tenant do rascunho (o servidor manda `id: null` a
  // quem não enxerga).
  const [wizardAberto, setWizardAberto] = useState(false)
  const wizard = proposedAction.action_type === 'terraform_stage_create' && isPending
    && typeof proposedAction.rascunho_tenant?.id === 'number'
    ? { tenantId: proposedAction.rascunho_tenant.id as number,
        rascunhoId: Number((proposedAction.params as Record<string, unknown>)?.rascunho_id) }
    : null
  // Review de consoleai#24896: a criação de ambiente confirma SÓ pelo wizard,
  // que lê a prontidão da conta antes. Ler a AWS em toda listagem de mensagens
  // para decidir o Confirmar daqui custaria ~10 chamadas por cartão; o executor
  // do servidor confere a prontidão de novo e recusa de qualquer jeito.
  const confirmaPeloWizard = proposedAction.action_type === 'terraform_stage_create'
  const { rotulo: rotuloDoSelo, cor: corDoSelo } = selo(proposedAction)

  // Handoff: abrir chamado leva à tela real (/chamados/novo) pré-preenchida.
  // Sem confirm/decline aqui — a criação e validação PCI acontecem na tela.
  // Branch fica APÓS todos os hooks (rules-of-hooks): as mutations acima ficam
  // criadas mas não usadas neste caminho — inócuo.
  if (proposedAction.action_type === 'open_chamado') {
    return comSelo(<TicketHandoffCard params={proposedAction.params} />)
  }

  // Relatório em PDF: o @Atendente já gerou o relatório (generate_report) e
  // propôs o download. Card dedicado com botão de download autenticado.
  if (proposedAction.action_type === 'download_report') {
    return (
      <AssistantReportCard
        reportId={proposedAction.params.report_id}
        filename={proposedAction.params.filename}
      />
    )
  }

  // O formulário desde a primeira anotação (consoleai#25762): abre o wizard do
  // rascunho; o Confirmar aparece nele quando o rascunho fica completo e o
  // servidor posta o cartão da criação (consoleai#26021).
  if (proposedAction.action_type === 'abrir_formulario_de_ambiente') {
    const rt = proposedAction.rascunho_tenant
    return (
      <FormularioDoAmbienteCard
        conversationId={conversationId}
        rascunhoId={Number(proposedAction.params.rascunho_id)}
        tenantId={typeof rt?.id === 'number' ? rt.id : null}
        projeto={proposedAction.params.projeto}
        ambiente={proposedAction.params.ambiente}
        passo={typeof proposedAction.params.passo === 'string' ? proposedAction.params.passo : undefined}
      />
    )
  }

  // Assessoria de chamados (MR-5): sugestão de comentário aplicada por um humano
  // com permissão de chamados (nunca pelo Atendente). Card + endpoint dedicados.
  if (proposedAction.action_type === 'chamado_comment') {
    return (
      <ChamadoCommentApplyCard
        conversationId={conversationId}
        messageId={messageId}
        proposedAction={proposedAction}
      />
    )
  }

  // Assumir/atribuir o chamado que está sendo trabalhado (TASK-11421): mesmo
  // endpoint dedicado do comentário, gate `chamados:assign` no servidor.
  if (proposedAction.action_type === 'chamado_assign') {
    return (
      <ChamadoAssignApplyCard
        conversationId={conversationId}
        messageId={messageId}
        proposedAction={proposedAction}
      />
    )
  }

  // Finalizar o chamado (spec 2026-10-16 §4.4): o Atendente redige o fechamento,
  // o atendente edita e finaliza pelo mesmo endpoint dedicado.
  if (proposedAction.action_type === 'chamado_finalizar') {
    return (
      <ChamadoFinalizarApplyCard
        conversationId={conversationId}
        messageId={messageId}
        proposedAction={proposedAction}
      />
    )
  }

  // Ordem no host (Task 9/10 backend, Task 11/TASK-10361 este cartão): o
  // plano de uma limpeza/poda irreversível — o que vai ser apagado (ou "sem
  // estimativa" quando o backend não enumera) e o botão que confirma.
  // `can_confirm` vem do SERVIDOR (nunca de `ACTION_GATES`, ver acima);
  // ausência do campo (proposta antiga, sem o enriquecimento da Task 10)
  // é tratada como `false` — deny-by-default, nunca fail-open.
  if (proposedAction.action_type === 'host_order') {
    const params = proposedAction.params as unknown as Record<string, unknown>
    return comSelo(
      <HostOrderPlanCard
        plano={hostOrderPlanoFromParams(params)}
        canConfirm={servidorDecidiu ? proposedAction.pode_confirmar === true : proposedAction.can_confirm === true}
        motivoBloqueio={proposedAction.motivo_bloqueio}
        hostname={hostOrderHostname(params)}
        isProd={hostOrderIsProd(params)}
        status={status}
        onConfirm={() => confirmMutation.mutate({ messageId })}
        isConfirming={confirmMutation.isPending}
        onDecline={() => declineMutation.mutate({ messageId })}
        isDeclining={declineMutation.isPending}
        resultado={proposedAction.result ?? null}
        tenantId={typeof params.tenant_id === 'number' ? params.tenant_id : null}
      />,
    )
  }

  // Ajuste de infraestrutura via Terraform (Task 13 backend, Task 14 este
  // cartão): o portão 2 do fluxo — diff, custo (com ressalva), avisos e o
  // repetir-valor de knob irreversível. `canConfirm` reusa o MESMO cálculo
  // de `ACTION_GATES` acima (papel devops + permissão `terraform:adjust`) —
  // nenhum mapa novo, mesma trava de `restart_container`/`instance_restart`.
  if (proposedAction.action_type === 'terraform_adjust') {
    const params = proposedAction.params as unknown as Record<string, unknown>
    // O 409 do cartão velho (Review Focus 2) volta com o estado atual: em
    // qualquer desfecho do clique — sucesso ou erro — relê o pedido.
    const relerPedido = () => {
      if (pedidoId != null) {
        void qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM, pedidoId] })
      }
    }
    return (
      <TerraformAdjustCard
        {...mapearTerraformAdjuste(params)}
        status={status}
        canConfirmPermission={canConfirm}
        pedido={pedidoId == null
          ? (proposedAction.motivo_bloqueio ? null : undefined)
          : pedidoQuery.data}
        pedidoCarregando={pedidoId != null && pedidoQuery.isLoading}
        // Fix round 1 (achado 1): há `pedido_id`, a primeira leitura já
        // terminou (`isLoading` falso) e ainda assim não há `data` — o GET
        // errou (ou um refetch em segundo plano ainda não teve sucesso desde
        // então). Sem este sinal separado, `pedido` chegava `undefined` e o
        // cartão confundia isto com "proposta anterior ao pedido_id".
        pedidoIndisponivel={pedidoId != null && !pedidoQuery.isLoading && pedidoQuery.data === undefined}
        motivoBloqueio={proposedAction.motivo_bloqueio ?? null}
        onConfirm={() => confirmMutation.mutate({ messageId }, { onSuccess: relerPedido, onError: relerPedido })}
        isConfirming={confirmMutation.isPending}
        onDecline={() => declineMutation.mutate({ messageId }, { onSuccess: relerPedido, onError: relerPedido })}
        isDeclining={declineMutation.isPending}
        onRetomar={retomar}
        isRetomando={retomarMutation.isPending}
        avisoFila={avisoFilaDe(proposedAction.params)}
      />
    )
  }

  // Ajuste livre (spec 2026-10-08 D11): arquivos inteiros escritos pelo
  // @DevOps. O cartão mostra arquivo e justificativa, nunca o HCL; o efeito
  // aparece pelo plan, no mesmo bloco do pedido do ajuste por knob.
  if (proposedAction.action_type === 'terraform_ajuste_livre') {
    const p = proposedAction.params as unknown as {
      stage_chave?: string; pedido_texto?: string; avisos?: string[]
      patch_livre?: { arquivos?: { arquivo: string; justificativa: string }[] }
    }
    const relerPedido = () => {
      if (pedidoId != null) {
        void qc.invalidateQueries({ queryKey: [CHAVE_PEDIDO_TERRAFORM, pedidoId] })
      }
    }
    return comSelo(
      <TerraformLivreCard
        stageChave={p.stage_chave ?? '?'}
        pedidoTexto={p.pedido_texto ?? ''}
        arquivos={(p.patch_livre?.arquivos ?? []).map(({ arquivo, justificativa }) => ({ arquivo, justificativa }))}
        avisos={p.avisos ?? []}
        status={status}
        canConfirmPermission={canConfirm}
        pedido={pedidoId == null
          ? (proposedAction.motivo_bloqueio ? null : undefined)
          : pedidoQuery.data}
        pedidoCarregando={pedidoId != null && pedidoQuery.isLoading}
        pedidoIndisponivel={pedidoId != null && !pedidoQuery.isLoading && pedidoQuery.data === undefined}
        motivoBloqueio={proposedAction.motivo_bloqueio ?? null}
        onConfirm={() => confirmMutation.mutate({ messageId }, { onSuccess: relerPedido, onError: relerPedido })}
        isConfirming={confirmMutation.isPending}
        onDecline={() => declineMutation.mutate({ messageId }, { onSuccess: relerPedido, onError: relerPedido })}
        isDeclining={declineMutation.isPending}
        onRetomar={retomar}
        isRetomando={retomarMutation.isPending}
        avisoFila={avisoFilaDe(proposedAction.params)}
      />,
    )
  }

  return comSelo(
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        // TASK-24879: com o andamento da criação aberto, os endereços de recurso
        // pedem largura — 480 px cortava todos no mesmo prefixo.
        maxWidth: isMobile ? '100%' : (pedidoDaCriacao.data ? 760 : 480),
      }}
    >
      {/* Cabeçalho: label da ação + badge de status */}
      <Group gap="xs" mb="xs" wrap="nowrap">
        <Text size="sm" fw={600} style={{ flex: 1 }}>
          {actionLabel(proposedAction)}
        </Text>
        {substituida ? (
          <Badge size="xs" color="gray" variant="light">Substituído</Badge>
        ) : (
          <Badge size="xs" color={corDoSelo} variant="light">
            {rotuloDoSelo}
          </Badge>
        )}
      </Group>
      {substituida && (
        <Text size="xs" c="dimmed" mb="xs">
          Este cartão foi substituído por um mais novo do mesmo pedido — confirme pelo mais recente.
        </Text>
      )}
      {!substituida && proposedAction.action_type === 'terraform_stage_create' && proposedAction.resumo && (
        <ResumoDaCriacao resumo={proposedAction.resumo} />
      )}
      {proposedAction.action_type === 'terraform_stage_create' && proposedAction.rascunho_tenant && (
        <Text size="xs" c="dimmed" ff="JetBrains Mono, monospace" mb="xs">
          {proposedAction.rascunho_tenant.nome
            ? `Tenant do pedido: ${proposedAction.rascunho_tenant.nome} (#${proposedAction.rascunho_tenant.id})`
            : 'Tenant do pedido: sem acesso'}
        </Text>
      )}
      {(proposedAction.action_type === 'terraform_stage_create'
        || proposedAction.action_type === 'terraform_conta_create')
        && proposedAction.pode_confirmar === false && proposedAction.motivo_bloqueio && (
        <Text size="xs" c="yellow.6" mb="xs">
          {`Ainda não dá para confirmar: ${proposedAction.motivo_bloqueio}`}
        </Text>
      )}

      {pedidoDaCriacao.data && <AndamentoDaCriacao pedido={pedidoDaCriacao.data} />}

      {wizard && Number.isFinite(wizard.rascunhoId) && (
        <WizardAmbiente
          aberto={wizardAberto}
          onFechar={() => setWizardAberto(false)}
          tenantId={wizard.tenantId}
          rascunhoId={wizard.rascunhoId}
          podeConfirmar={canConfirm}
          motivoBloqueio={proposedAction.motivo_bloqueio ?? null}
          onConfirmar={() => confirmMutation.mutate({ messageId })}
          confirmando={confirmMutation.isPending}
        />
      )}

      {/* Botões de ação — somente quando status é pending */}
      {isPending && (
        <Group gap="xs" mt="xs" wrap={isMobile ? 'wrap' : 'nowrap'}>
          {wizard && Number.isFinite(wizard.rascunhoId) && (
            <Button size="xs" variant="default"
                    leftSection={<IconListCheck size={14} stroke={1.5} />}
                    onClick={() => setWizardAberto(true)}>
              Revisar e confirmar
            </Button>
          )}
          {confirmaPeloWizard ? (
            <Text size="xs" c="dimmed">Confirme em "Revisar e confirmar": lá a conta é conferida antes.</Text>
          ) : (
          <Tooltip
            // TASK-10810 (I3 da rodada 1 de revisão): quando o veto é o
            // portão de PRD, mostra o MOTIVO real ("preciso do laudo do
            // @DevOps..."), não o texto genérico de papel/permissão — sem
            // isto o usuário perdia o botão sem saber por quê, e a frase que
            // converteria o bloqueio em ação (pedir o laudo) ficava write-only.
            label={
              bloqueadoPeloPortaoPrd
                ? (proposedAction.motivo_bloqueio || 'Ambiente de produção: requer laudo do @DevOps.')
                : servidorDecidiu
                  // O servidor pode dizer POR QUE não (precondição do ambiente,
                  // spec 2026-09-29 D9); sem motivo, é papel/permissão.
                  ? (proposedAction.motivo_bloqueio || 'Sua conta não pode confirmar esta ação neste tenant.')
                  : 'Requer DevOps + permissão'
            }
            disabled={canConfirm || !!gate?.selfScoped}
            withArrow
            position="top"
          >
            {/* wrapper div necessário para o Tooltip funcionar com botão desabilitado */}
            <div>
              <Button
                size="xs"
                color="inovareTeal"
                variant="filled"
                disabled={!canConfirm}
                loading={confirmMutation.isPending}
                onClick={() => confirmMutation.mutate({ messageId })}
              >
                Confirmar
              </Button>
            </div>
          </Tooltip>
          )}

          <Button
            size="xs"
            color="red"
            variant="light"
            loading={declineMutation.isPending}
            onClick={() => declineMutation.mutate({ messageId })}
          >
            Recusar
          </Button>
        </Group>
      )}
    </Card>,
  )
}
